import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, Sparkles, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useUser } from '../contexts/UserContext';
import MimoCharacter from '../components/MimoCharacter';

const ageModes = [
  { 
    id: 'explorador', 
    label: 'Explorador', 
    age: '5-10 años',
    description: 'MIMO es más colorido y divertido. Te acompaña con cuentos y juegos.',
    color: '#FFD166',
    icon: '🌟'
  },
  { 
    id: 'companero', 
    label: 'Compañero', 
    age: '11-17 años',
    description: 'MIMO es tu amigo de confianza. Te ayuda con estudios y emociones.',
    color: '#4F8CFF',
    icon: '🎯'
  },
  { 
    id: 'aliado', 
    label: 'Aliado', 
    age: '18+ años',
    description: 'MIMO es elegante y productivo. Te ayuda con hábitos y organización.',
    color: '#06D6A0',
    icon: '✨'
  }
];

const hobbies = [
  { id: 'deportes', label: 'Deportes', emoji: '⚽' },
  { id: 'musica', label: 'Música', emoji: '🎵' },
  { id: 'arte', label: 'Arte', emoji: '🎨' },
  { id: 'gaming', label: 'Gaming', emoji: '🎮' },
  { id: 'lectura', label: 'Lectura', emoji: '📚' },
  { id: 'cocina', label: 'Cocina', emoji: '🍳' },
  { id: 'tecnologia', label: 'Tecnología', emoji: '💻' },
  { id: 'naturaleza', label: 'Naturaleza', emoji: '🌿' },
  { id: 'fitness', label: 'Fitness', emoji: '💪' },
  { id: 'meditacion', label: 'Meditación', emoji: '🧘' },
  { id: 'escritura', label: 'Escritura', emoji: '✍️' },
  { id: 'fotografia', label: 'Fotografía', emoji: '📷' }
];

const energyTypes = [
  { id: 'focus', label: 'Enfoque', emoji: '🧠', desc: 'Te energizas al concentrarte' },
  { id: 'movement', label: 'Movimiento', emoji: '💪', desc: 'Te energizas al moverte' },
  { id: 'creativity', label: 'Creatividad', emoji: '🎨', desc: 'Te energizas al crear' },
  { id: 'learning', label: 'Aprendizaje', emoji: '📖', desc: 'Te energizas al aprender' },
  { id: 'gratitude', label: 'Gratitud', emoji: '❤️', desc: 'Te energizas al agradecer' }
];

export const Onboarding = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { updateUser, setUser } = useUser();
  
  const [step, setStep] = useState(0);
  const [selectedAge, setSelectedAge] = useState('aliado');
  const [selectedHobbies, setSelectedHobbies] = useState([]);
  const [selectedEnergy, setSelectedEnergy] = useState('focus');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const totalSteps = 3;

  const toggleHobby = (hobbyId) => {
    setSelectedHobbies(prev => 
      prev.includes(hobbyId) 
        ? prev.filter(h => h !== hobbyId)
        : [...prev, hobbyId]
    );
  };

  const handleNext = () => {
    if (step < totalSteps - 1) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const handleComplete = async () => {
    setIsSubmitting(true);
    try {
      const updatedUser = await updateUser({
        age_mode: selectedAge,
        hobbies: selectedHobbies,
        favorite_energy: selectedEnergy,
        onboarded: true
      });
      setUser(updatedUser);
      navigate('/dashboard');
    } catch (error) {
      console.error('Onboarding error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-8"
          >
            <div className="text-center">
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
                ¿Qué edad tienes?
              </h2>
              <p className="text-muted-foreground">
                MIMO se adaptará a tu estilo
              </p>
            </div>

            <div className="grid gap-4">
              {ageModes.map((mode) => (
                <motion.button
                  key={mode.id}
                  onClick={() => setSelectedAge(mode.id)}
                  className={`p-6 rounded-2xl text-left transition-all ${
                    selectedAge === mode.id
                      ? 'ring-2'
                      : 'glass hover:bg-muted/50'
                  }`}
                  style={{
                    ringColor: selectedAge === mode.id ? mode.color : undefined,
                    backgroundColor: selectedAge === mode.id ? `${mode.color}10` : undefined
                  }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  data-testid={`age-${mode.id}`}
                >
                  <div className="flex items-center gap-4">
                    <span className="text-3xl">{mode.icon}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-foreground">{mode.label}</span>
                        <span className="text-sm text-muted-foreground">({mode.age})</span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">{mode.description}</p>
                    </div>
                    {selectedAge === mode.id && (
                      <div 
                        className="w-6 h-6 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: mode.color }}
                      >
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>
                </motion.button>
              ))}
            </div>
          </motion.div>
        );

      case 1:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-8"
          >
            <div className="text-center">
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
                ¿Qué te gusta hacer?
              </h2>
              <p className="text-muted-foreground">
                Selecciona tus hobbies favoritos
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {hobbies.map((hobby) => (
                <motion.button
                  key={hobby.id}
                  onClick={() => toggleHobby(hobby.id)}
                  className={`p-4 rounded-xl text-center transition-all ${
                    selectedHobbies.includes(hobby.id)
                      ? 'bg-[#FF7A59]/10 ring-2 ring-[#FF7A59]'
                      : 'glass hover:bg-muted/50'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  data-testid={`hobby-${hobby.id}`}
                >
                  <span className="text-2xl">{hobby.emoji}</span>
                  <p className={`text-sm mt-1 ${
                    selectedHobbies.includes(hobby.id) 
                      ? 'font-semibold text-[#FF7A59]' 
                      : 'text-muted-foreground'
                  }`}>
                    {hobby.label}
                  </p>
                </motion.button>
              ))}
            </div>

            <p className="text-center text-sm text-muted-foreground">
              {selectedHobbies.length} seleccionados
            </p>
          </motion.div>
        );

      case 2:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-8"
          >
            <div className="text-center">
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
                ¿Qué te da energía?
              </h2>
              <p className="text-muted-foreground">
                MIMO celebrará tus logros de forma especial
              </p>
            </div>

            <div className="grid gap-3">
              {energyTypes.map((energy) => (
                <motion.button
                  key={energy.id}
                  onClick={() => setSelectedEnergy(energy.id)}
                  className={`p-4 rounded-xl flex items-center gap-4 transition-all ${
                    selectedEnergy === energy.id
                      ? 'bg-[#4F8CFF]/10 ring-2 ring-[#4F8CFF]'
                      : 'glass hover:bg-muted/50'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  data-testid={`energy-${energy.id}`}
                >
                  <span className="text-2xl">{energy.emoji}</span>
                  <div className="text-left flex-1">
                    <span className={`font-medium ${
                      selectedEnergy === energy.id ? 'text-[#4F8CFF]' : 'text-foreground'
                    }`}>
                      {energy.label}
                    </span>
                    <p className="text-sm text-muted-foreground">{energy.desc}</p>
                  </div>
                  {selectedEnergy === energy.id && (
                    <div className="w-6 h-6 rounded-full bg-[#4F8CFF] flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}
                </motion.button>
              ))}
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-lg">
        {/* Progress */}
        <div className="flex items-center gap-2 mb-8">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div
              key={i}
              className={`flex-1 h-1.5 rounded-full transition-colors ${
                i <= step ? 'bg-[#FF7A59]' : 'bg-muted'
              }`}
            />
          ))}
        </div>

        {/* MIMO */}
        <div className="flex justify-center mb-8">
          <MimoCharacter mood={step === 2 ? 'excited' : 'happy'} size="md" />
        </div>

        {/* Steps */}
        <AnimatePresence mode="wait">
          {renderStep()}
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8">
          {step > 0 ? (
            <Button
              variant="ghost"
              onClick={handleBack}
              data-testid="onboarding-back"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Atrás
            </Button>
          ) : (
            <div />
          )}

          {step < totalSteps - 1 ? (
            <Button
              onClick={handleNext}
              className="bg-[#FF7A59] hover:bg-[#E66A4B] text-white rounded-full px-6"
              data-testid="onboarding-next"
            >
              Siguiente
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleComplete}
              disabled={isSubmitting}
              className="bg-[#FF7A59] hover:bg-[#E66A4B] text-white rounded-full px-6"
              data-testid="onboarding-complete"
            >
              {isSubmitting ? (
                'Guardando...'
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  ¡Comenzar!
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
